import React, { FC, forwardRef, DetailedHTMLProps, InputHTMLAttributes, TextareaHTMLAttributes } from 'react';

export type InputSize = 'medium' | 'large';
export type InputType = 'text' | 'email';

export type InputProps = {
    id: string;
    name: string;
    label: string;
    showLabel?: boolean; // 🔹 Nueva prop
    type?: InputType;
    size?: InputSize;
    className?: string;
    classNameLabel?: string;
    error?: boolean;
    as?: 'input' | 'textarea';
    isRequired?: boolean;
} & Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> &
    DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
    'size'
>;

export const Input: FC<InputProps> = forwardRef<HTMLInputElement & HTMLTextAreaElement, InputProps>(
    (
        {
            id,
            name,
            label,
            showLabel = true,
            type = 'text',
            size = 'medium',
            className = '',
            classNameLabel = 'text-system-light-40',
            placeholder,
            error,
            isRequired,
            as = 'input',
            ...props
        },
        ref
    ) => {
        const sizeClasses = size === 'large'
            ? 'h-16 text-blarge px-6 py-4'
            : 'h-12 text-bmedium px-4 py-3';

        const baseClasses = `
            relative w-full rounded-sm placeholder-system-light-40
            border hover:border-system-light-100 hover:brightness-115 transform-all duration-200 focus:outline-none focus:border-primary-40
            ${error ? 'border-brand-green-40' : 'border-system-light-50 '}
            ${sizeClasses}
            ${className}
        `;

        return (
            <div className="w-full flex flex-col">
                {showLabel && (
                    <label htmlFor={id} className={`${classNameLabel} text-bsmall mb-1`}>
                        {label}
                        {isRequired && <span className="text-error-40 ml-1">*</span>}
                    </label>
                )}

                {as === 'input' ? (
                    <input
                        id={id}
                        name={name}
                        type={type}
                        placeholder={placeholder}
                        aria-label={!showLabel ? label : undefined} // 🔹 accesibilidad
                        ref={ref as React.Ref<HTMLInputElement>}
                        className={baseClasses}
                        {...props}
                    />
                ) : (
                    <textarea
                        id={id}
                        name={name}
                        placeholder={placeholder}
                        aria-label={!showLabel ? label : undefined}
                        ref={ref as React.Ref<HTMLTextAreaElement>}
                        rows={6}
                        className={baseClasses}
                        {...props}
                    />
                )}
            </div>
        );
    }
);